import { useEffect, useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { format } from 'date-fns'
import { es as esDateFns } from 'date-fns/locale'
import { CalendarIcon, Info, Trash2, Volume2 } from 'lucide-react'
import { DateTime } from 'luxon'
import { es as esRdp } from 'react-day-picker/locale'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { RRule } from 'rrule'
import { z } from 'zod'

import { Playlist } from '@/core/api/entities/playlist/playlist'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { Button } from '@/shared/components/atoms/Button'
import { Checkbox } from '@/shared/components/atoms/Checkbox'
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/shared/components/atoms/Form'
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/shared/components/atoms/Popover'
import { ScrollArea } from '@/shared/components/atoms/Scroll'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Separator } from '@/shared/components/atoms/Separator'
import { Slider } from '@/shared/components/atoms/Slider'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/shared/components/molecules/AlertDialog'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/shared/components/molecules/Dialog/dialog'
import { Calendar } from '@/shared/components/organisms/Calendar/Calendar'
import { useNotificationContext } from '@/shared/context/NotificationProvider'

import { CalendarEvent } from '../../context/Data'
import { useEvents } from '../../context/EventContext'
import { usePlaylistData } from '../../hooks/usePlaylistData'
import { applyOverlapResolution } from '../../utils/applyOverlapResolution'
import {
	findOverlappingEvents,
	OverlapDetails,
} from '../../utils/priorityCalculator'
import { getDaysOfWeek, toLocalISOString } from '../../utils/shared'
import { EditOverlapConfirmationModal } from '../EditOverlapConfirmationModal/EditOverlapConfirmationModal'
import { MultiOverlapWarningModal } from '../MultiOverlapWarningModal/MultiOverlapWarningModal'
import { PlaylistSelector } from '../PlaylistSelector'
import { RepeatButton } from '../RepeatButton'

const eventEditFormSchema = z
	.object({
		date: z.date({
			message: "That's not a valid date!",
		}),
		customEndDate: z.boolean(),
		endDate: z.date().optional(),
		startTime: z.string().min(1, { message: 'Select a start time' }),
		endTime: z.string().min(1, { message: 'Select an end time' }),
		repeatDays: z.array(z.string()),
		playlists: z
			.array(z.number())
			.min(1, { message: 'Select at least 1 playlist' })
			.max(2, { message: 'Select a maximum of 2 playlists' }),
		playlistPercentages: z
			.record(z.string(), z.number().min(0).max(100))
			.optional(),
		volume: z.number().min(0).max(100),
	})
	.refine(
		(data) => {
			if (data.customEndDate && !data.endDate) return false
			return true
		},
		{
			message: 'End date is required when custom end date is enabled',
			path: ['endDate'],
		}
	)

type EventEditFormValues = z.infer<typeof eventEditFormSchema>

interface EventEditFormProps {
	oldEvent?: CalendarEvent
	event?: CalendarEvent
	isDrag: boolean
	displayButton: boolean
}

// Generate time options in 15-minute intervals
const generateTimeOptions = () => {
	const times: string[] = []
	for (let hour = 0; hour < 24; hour++) {
		for (let minute = 0; minute < 60; minute += 15) {
			const time24 = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
			times.push(time24)
		}
	}
	return times
}

/**
 * Calculates the next valid occurrence date based on RRULE
 * Used when splitting timeslots after deleting an instance
 */
const calculateNextOccurrence = (fromDate: Date, rrule: any): Date => {
	const result = new Date(fromDate)

	if (!rrule || !rrule.byweekday) {
		result.setDate(result.getDate() + 1)
		return result
	}

	// Convert byweekday array to Set for O(1) lookup
	const validDays = new Set(
		Array.isArray(rrule.byweekday) ? rrule.byweekday : [rrule.byweekday]
	)

	// Search for next valid day (max 7 days to avoid infinite loop)
	for (let i = 1; i <= 7; i++) {
		const testDate = new Date(fromDate)
		testDate.setDate(testDate.getDate() + i)

		const dayOfWeek = testDate.getDay()
		const rruleDayOfWeek = dayOfWeek === 0 ? 6 : dayOfWeek - 1

		if (validDays.has(rruleDayOfWeek)) {
			return testDate
		}
	}

	result.setDate(result.getDate() + 1)
	return result
}

/**
 * Generates a unique ID for a timeslot
 */
const generateTimeslotId = (): string => {
	return `timeslot-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
}

export const EventEditForm = ({
	oldEvent,
	event,
	isDrag,
}: EventEditFormProps) => {
	const { t } = useTranslation()
	const { language } = useSelector((state: RootState) => state.ui)
	const {
		events,
		addEvent,
		deleteEvent,
		updateEvent,
		eventEditOpen,
		setEventEditOpen,
		setEventViewOpen,
	} = useEvents()
	const { showNotification } = useNotificationContext()
	const [multiOverlapWarningOpen, setMultiOverlapWarningOpen] = useState(false)
	const [multiOverlapDetails, setMultiOverlapDetails] = useState<
		OverlapDetails[]
	>([])
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
	const [deleteOption, setDeleteOption] = useState<
		'this' | 'following' | 'all'
	>('this')
	// Overlap confirmation state
	const [editOverlapModalOpen, setEditOverlapModalOpen] = useState(false)
	const [editOverlapDetails, setEditOverlapDetails] =
		useState<OverlapDetails | null>(null)
	const [pendingFormData, setPendingFormData] =
		useState<EventEditFormValues | null>(null)

	// Track selected playlist entities locally
	const [selectedPlaylistsMap, setSelectedPlaylistsMap] = useState<
		Record<number, Playlist>
	>({})

	// Build selected playlists from selectedPlaylistsMap OR create stubs from event.playlists
	// This must not reference allPlaylists to avoid circular dependency
	const selectedPlaylists = useMemo(() => {
		const fromMap = Object.values(selectedPlaylistsMap)

		// If we have playlists in the map, use those
		if (fromMap.length > 0) {
			return fromMap
		}

		// Otherwise, if we have an event with playlists but no map yet,
		// create stub entities so they can be found by the hook
		if (event?.playlists && event.playlists.length > 0) {
			return event.playlists.map((p) => {
				// Create a stub entity with minimal data
				// The hook will merge this into results so it's visible
				return Playlist.create({
					id: p.id,
					title: t('entities.schedule.event_view.playlist_stub', {
						id: p.id,
					}), // Will be replaced when we find the real one
					percentage: p.percentage,
				})
			})
		}

		return []
	}, [selectedPlaylistsMap, event?.playlists, t])

	// Use shared playlist data hook with selected playlists
	const {
		allPlaylists,
		isLoading,
		isFetching,
		hasMorePages,
		loadMore,
		searchQuery,
		setSearchQuery,
		customPlaylistIds,
	} = usePlaylistData({
		dialogOpen: eventEditOpen,
		selectedPlaylists,
	})

	// Reset selectedPlaylistsMap when dialog closes
	useEffect(() => {
		if (!eventEditOpen) {
			setSelectedPlaylistsMap({})
		}
	}, [eventEditOpen])

	const daysOfWeek = getDaysOfWeek(t)

	const form = useForm<z.infer<typeof eventEditFormSchema>>({
		resolver: zodResolver(eventEditFormSchema),
		defaultValues: {
			playlists: [],
			customEndDate: false,
			repeatDays: [],
			playlistPercentages: {},
			volume: 50,
		},
	})

	const handleEditCancellation = () => {
		if (isDrag && oldEvent) {
			if (!event?.rrule && !oldEvent?.rrule) {
				// Restore the event to its original position without deleting first
				// (updateEvent only updates existing events, it won't re-add a deleted one)
				updateEvent(event!.id, {
					start: oldEvent.start,
					end: oldEvent.end,
				})
			}
		}
		setEventEditOpen(false)
	}

	useEffect(() => {
		if (eventEditOpen && event) {
			const startTime = `${String(event.start.getHours()).padStart(2, '0')}:${String(event.start.getMinutes()).padStart(2, '0')}`
			const endTime = `${String(event.end.getHours()).padStart(2, '0')}:${String(event.end.getMinutes()).padStart(2, '0')}`

			// Build playlist percentages object if event has playlists
			const playlistPercentages: Record<number, number> = {}
			if (event.playlists && event.playlists.length === 2) {
				event.playlists.forEach((p) => {
					playlistPercentages[p.id] = p.percentage
				})
			}

			// For recurring events, use the original rrule date, not the instance date
			let formDate = event.start
			if (event.rrule && event.rrule.dtstart) {
				formDate = new Date(event.rrule.dtstart)
			}

			// Check if the event has a custom end date (has until in rrule)
			let hasCustomEndDate = false
			let endDateValue: Date | undefined = undefined
			if (event.rrule && event.rrule.until) {
				hasCustomEndDate = true
				endDateValue = new Date(event.rrule.until)
			}

			form.reset({
				date: formDate,
				customEndDate: hasCustomEndDate,
				endDate: endDateValue,
				startTime: startTime,
				endTime: endTime,
				repeatDays: event.repeatDays || [],
				playlists: event.playlists?.map((p) => p.id) || [],
				playlistPercentages: playlistPercentages,
				volume: (event as any).volume ?? 50,
			})
		}
	}, [eventEditOpen, event, form])

	const handleDeleteClick = () => {
		// If it's a single event (no repetition), delete directly without popup
		if (!event?.repeatDays || event.repeatDays.length === 0) {
			setDeleteOption('this')
			handleDelete()
		} else {
			// Show popup for recurring events
			setDeleteDialogOpen(true)
		}
	}

	const handleDelete = () => {
		if (!event) return

		// Find the original event with rrule from the events array
		const originalEvent = events.find((e) => e.id === event.id)

		// If it's not a recurring event, always treat it as 'this'
		const isRecurring =
			!!originalEvent?.rrule ||
			(originalEvent?.repeatDays && originalEvent.repeatDays.length > 0)
		const effectiveDeleteOption = isRecurring ? deleteOption : 'this'

		switch (effectiveDeleteOption) {
			case 'this':
				// Non-recurring event: delete normally
				if (!originalEvent?.rrule) {
					deleteEvent(event.id)
					break
				}

				// Recurring event: SPLIT THE TIMESLOT
				if (event.start && originalEvent?.rrule) {
					// Get the date of the instance to delete
					const deleteDate = new Date(event.start)

					// Get time from original rrule dtstart
					const dtstartDate = new Date(originalEvent.rrule.dtstart)
					const hours = dtstartDate.getHours()
					const minutes = dtstartDate.getMinutes()
					const seconds = dtstartDate.getSeconds()

					// Set the time on delete date to match dtstart time
					deleteDate.setHours(hours, minutes, seconds, 0)

					// Calculate dates for splitting
					const dayBeforeDelete = new Date(deleteDate)
					dayBeforeDelete.setDate(dayBeforeDelete.getDate() - 1)
					dayBeforeDelete.setHours(23, 59, 59, 999)

					// Find next valid occurrence according to rrule
					const nextOccurrence = calculateNextOccurrence(
						deleteDate,
						originalEvent.rrule
					)
					nextOccurrence.setHours(hours, minutes, seconds, 0)

					// Clean rrule object - only keep essential properties
					const cleanRRule = {
						freq: originalEvent.rrule.freq,
						...(originalEvent.rrule.byweekday
							? { byweekday: originalEvent.rrule.byweekday }
							: {}),
						...(originalEvent.rrule.until &&
						originalEvent.rrule.until !== toLocalISOString(dayBeforeDelete)
							? { until: originalEvent.rrule.until }
							: {}),
					}

					// Create TIMESLOT 1: From original start UNTIL day before delete
					const timeslot1: CalendarEvent = {
						...originalEvent,
						id: generateTimeslotId(),
						rrule: {
							...cleanRRule,
							dtstart: originalEvent.rrule.dtstart,
							until: toLocalISOString(dayBeforeDelete),
						},
					}

					// Create TIMESLOT 2: From next occurrence onwards
					const timeslot2: CalendarEvent = {
						...originalEvent,
						id: generateTimeslotId(),
						rrule: {
							...cleanRRule,
							dtstart: toLocalISOString(nextOccurrence),
							// Keep original until if it existed
							...(originalEvent.rrule.until
								? { until: originalEvent.rrule.until }
								: {}),
						},
					}

					// Delete original timeslot
					deleteEvent(originalEvent.id)

					// Only add the first part if it has at least one occurrence
					// (dayBeforeDelete must be >= original dtstart)
					if (dayBeforeDelete.getTime() >= dtstartDate.getTime()) {
						addEvent(timeslot1)
					}

					// Only add the second part if it hasn't passed the original UNTIL date
					let isPart2Valid = true
					if (originalEvent.rrule.until) {
						const originalUntilDate = new Date(originalEvent.rrule.until)
						if (nextOccurrence.getTime() > originalUntilDate.getTime()) {
							isPart2Valid = false
						}
					}

					if (isPart2Valid) {
						addEvent(timeslot2)
					}

					showNotification({
						type: 'success',
						message: t('entities.schedule.calendar.event_instance_excluded'),
						description: t(
							'entities.schedule.calendar.timeslot_split_description'
						),
					})
				}
				break
			case 'following':
				// Delete this and following events by setting until date in rrule
				if (event.start && originalEvent?.rrule) {
					const untilDate = new Date(event.start)
					untilDate.setDate(untilDate.getDate() - 1) // Set until to day before this event

					// If the until date is before the original start of the recurrence,
					// it means we are deleting everything, so just delete the event.
					const recurrenceStart = new Date(originalEvent.rrule.dtstart)
					if (untilDate.getTime() < recurrenceStart.getTime()) {
						deleteEvent(originalEvent.id)
						break
					}

					// Format until date as ISO string
					const formattedUntil = toLocalISOString(untilDate)

					// Delete and re-add the event to force FullCalendar update
					deleteEvent(originalEvent.id)
					const updatedEvent = {
						...originalEvent,
						rrule: {
							...originalEvent.rrule,
							until: formattedUntil,
						},
					}

					addEvent(updatedEvent)
				}
				break
			case 'all':
				deleteEvent(event.id)
				break
		}

		setDeleteDialogOpen(false)
		setEventEditOpen(false)
		setEventViewOpen(false)
	}

	/**
	 * Builds the updated event object from form data
	 */
	function buildUpdatedEvent(data: EventEditFormValues): {
		updatedEvent: Partial<CalendarEvent>
		startDate: Date
		endDate: Date
	} {
		const [startHour, startMinute] = data.startTime.split(':').map(Number)
		const [endHour, endMinute] = data.endTime.split(':').map(Number)

		const playlistsWithPercentage = data.playlists.map((id) => {
			const percentage =
				data.playlistPercentages?.[id] ||
				Math.floor(100 / data.playlists.length)
			return {
				id: id,
				percentage: percentage,
			}
		})

		// Generate title from selected playlist names
		const playlistNames = data.playlists
			.map((id) => allPlaylists.find((p) => p.id === id)?.title)
			.filter(Boolean) as string[]

		const slotTitle =
			playlistNames.length > 0
				? playlistNames.join(' + ')
				: t('entities.schedule.slot_form.regular_slot')

		const startDate = new Date(data.date)
		startDate.setHours(startHour, startMinute, 0, 0)

		const endDate = new Date(data.date)
		endDate.setHours(endHour, endMinute, 0, 0)

		if (endDate <= startDate) {
			endDate.setDate(endDate.getDate() + 1)
		}

		const durationMs = endDate.getTime() - startDate.getTime()

		const updatedEvent: Partial<CalendarEvent> = {
			title: slotTitle,
			description:
				data.repeatDays.length > 0
					? `Repeats on: ${data.repeatDays.join(', ')}`
					: 'One-time slot',
			color: event?.color,
			backgroundColor: event?.backgroundColor,
			playlists: playlistsWithPercentage,
			repeatDays: data.repeatDays,
			// @ts-ignore
			volume: data.volume,
		}

		if (data.repeatDays.length > 0) {
			let rruleDays: any[] = []
			const isEveryday = data.repeatDays.includes('everyday')

			if (isEveryday) {
				rruleDays = [
					// @ts-ignore
					RRule.MO,
					// @ts-ignore
					RRule.TU,
					// @ts-ignore
					RRule.WE,
					// @ts-ignore
					RRule.TH,
					// @ts-ignore
					RRule.FR,
					// @ts-ignore
					RRule.SA,
					// @ts-ignore
					RRule.SU,
				]
			} else {
				rruleDays = data.repeatDays
					.map((day) => daysOfWeek.find((d) => d.value === day)?.rruleDay)
					.filter(Boolean)
			}

			const originalEventFromContext = events.find((e) => e.id === event?.id)
			let dtstartDate = startDate

			if (
				isDrag &&
				originalEventFromContext?.rrule &&
				originalEventFromContext.rrule.dtstart
			) {
				const originalDtstart = new Date(originalEventFromContext.rrule.dtstart)
				dtstartDate = new Date(originalDtstart)
				dtstartDate.setHours(startHour, startMinute, 0, 0)
			}

			updatedEvent.rrule = {
				// While we should use WEEKLY, if the event is recurring everyday, we use DAILY because all flutter versions
				// have compatibility issues at this moment. So, in order to prevent easily the bug in the app, since the very generation,
				// we use DAILY instead of WEEKLY.
				// Once we have a +3.1 version in every device, we will switch to WEEKLY properly or delegate the conversion to the backend.
				freq: RRule.DAILY,
				dtstart: toLocalISOString(dtstartDate),
			} as any
			// Only add byweekday if not everyday
			if (!isEveryday) {
				updatedEvent.rrule.byweekday = rruleDays.map((day: any) => day.weekday)
			}
			// Add until if customEndDate is set, otherwise explicitly don't set it
			// (removing any previous until value)
			if (data.customEndDate && data.endDate) {
				const untilDate = new Date(data.endDate)
				// Set until to 23:59:59 of that day to include the entire day
				untilDate.setHours(23, 59, 59, 999)
				updatedEvent.rrule.until = toLocalISOString(untilDate)
			}
			// Note: When customEndDate is false, we intentionally DON'T set until,
			// allowing the user to remove a previously set end date
			updatedEvent.duration = { milliseconds: durationMs } as any
			updatedEvent.startTime =
				`${String(startHour).padStart(2, '0')}:${String(startMinute).padStart(2, '0')}:00` as any
			updatedEvent.start = undefined
			updatedEvent.end = undefined
		} else {
			updatedEvent.start = startDate
			updatedEvent.end = endDate
			updatedEvent.rrule = undefined
			updatedEvent.duration = undefined
			updatedEvent.startTime = undefined as any
		}

		return { updatedEvent, startDate, endDate }
	}

	/**
	 * Applies the event update (called after overlap confirmation or when no overlap)
	 */
	function applyEventUpdate(data: EventEditFormValues) {
		if (!event) return

		const { updatedEvent } = buildUpdatedEvent(data)
		updateEvent(event.id, updatedEvent)
		setEventEditOpen(false)
		setEventViewOpen(false)
	}

	/**
	 * Handles confirming the overlap update (adjust the affected slot)
	 */
	function handleOverlapConfirmUpdate() {
		if (!pendingFormData || !editOverlapDetails || !event) return

		const { startDate, endDate } = buildUpdatedEvent(pendingFormData)
		applyOverlapResolution({
			details: editOverlapDetails,
			newSlotIsRecurring: (pendingFormData.repeatDays?.length ?? 0) > 0,
			newSlotStart: startDate,
			newSlotEnd: endDate,
			updateEvent,
			addEvent,
			deleteEvent,
		})

		// Now apply the original edit
		applyEventUpdate(pendingFormData)

		// Clear the overlap state
		setEditOverlapModalOpen(false)
		setEditOverlapDetails(null)
		setPendingFormData(null)
	}

	/**
	 * Handles confirming the overlap delete (delete the completely covered slot)
	 */
	function handleOverlapConfirmDelete() {
		if (!pendingFormData || !editOverlapDetails || !event) return

		// Delete the completely covered slot
		deleteEvent(editOverlapDetails.existingEvent.id)

		// Now apply the original edit
		applyEventUpdate(pendingFormData)

		// Clear the overlap state
		setEditOverlapModalOpen(false)
		setEditOverlapDetails(null)
		setPendingFormData(null)
	}

	/**
	 * Handles cancelling the overlap confirmation
	 */
	function handleOverlapCancel() {
		setEditOverlapModalOpen(false)
		setEditOverlapDetails(null)
		setPendingFormData(null)
	}

	function handleBackMultiOverlap() {
		setMultiOverlapWarningOpen(false)
		setMultiOverlapDetails([])
		setPendingFormData(null)
	}

	function handleContinueMultiOverlap() {
		if (!pendingFormData || !event) return
		if (multiOverlapDetails.length < 2) return

		const { startDate, endDate } = buildUpdatedEvent(pendingFormData)

		// Apply overlap resolution for each colliding existing segment.
		multiOverlapDetails.forEach((details) => {
			applyOverlapResolution({
				details,
				newSlotIsRecurring: (pendingFormData.repeatDays?.length ?? 0) > 0,
				newSlotStart: startDate,
				newSlotEnd: endDate,
				updateEvent,
				addEvent,
				deleteEvent,
			})
		})

		// Now apply the original edit
		applyEventUpdate(pendingFormData)

		setMultiOverlapWarningOpen(false)
		setMultiOverlapDetails([])
		setEditOverlapDetails(null)
		setPendingFormData(null)
	}

	async function onSubmit(data: EventEditFormValues) {
		if (!event) return

		const { startDate, endDate, updatedEvent } = buildUpdatedEvent(data)

		// Check for overlaps with other events (excluding the current event being edited).
		// IMPORTANT: for recurring slots with an end date (rrule.until), we must include the RRULE
		// in the draft. Otherwise the overlap detector will treat it as an infinite recurrence
		// (repeatDays-only) and report false overlaps after the series end.
		const newSlotDraft = {
			start: startDate,
			end: endDate,
			repeatDays: data.repeatDays,
			rrule: (updatedEvent as any).rrule,
		}
		const overlaps = findOverlappingEvents(
			newSlotDraft as any,
			events,
			event.id
		)

		if (overlaps.length > 1) {
			// Multiple overlaps are now handled via a warning modal (see below)
			setPendingFormData(data)
			setMultiOverlapDetails(overlaps)
			setMultiOverlapWarningOpen(true)
			return
		}

		if (overlaps.length === 1) {
			// Single overlap - show confirmation modal
			setPendingFormData(data)
			setEditOverlapDetails(overlaps[0])
			setEditOverlapModalOpen(true)
			return
		}

		// No overlaps - proceed with update
		applyEventUpdate(data)
	}

	return (
		<>
			<Dialog open={eventEditOpen} onOpenChange={setEventEditOpen}>
				<DialogContent
					scrollable={false}
					className="max-w-2xl h-auto p-0 gap-0"
				>
					<DialogHeader className="px-6 pt-6 pb-4">
						<DialogTitle className="text-xl">
							{t('entities.schedule.slot_form.edit_regular_slot')}
						</DialogTitle>
						<DialogDescription className="sr-only">
							{t('entities.schedule.slot_form.edit_slot_description')}
						</DialogDescription>
					</DialogHeader>

					<Separator />

					<Form {...form}>
						<form
							onSubmit={form.handleSubmit(onSubmit as any)}
							className="flex flex-col"
						>
							{/* Date and Time Section */}
							<div className="px-6 py-4 space-y-3">
								{/* Date and Time in one row */}
								<div className="flex items-center gap-2 text-sm">
									{/* Date Picker */}
									<FormField
										control={form.control as any}
										name="date"
										render={({ field }) => (
											<FormItem>
												<Popover>
													<PopoverTrigger asChild>
														<FormControl>
															<Button
																variant="ghost"
																className="h-auto p-0 hover:bg-transparent font-normal"
															>
																{field.value ? (
																	format(field.value, 'EEEE, d MMM', {
																		locale: language?.startsWith('es')
																			? esDateFns
																			: undefined,
																	})
																) : (
																	<span>
																		{t(
																			'entities.schedule.slot_form.pick_a_date'
																		)}
																	</span>
																)}
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0" align="start">
														<Calendar
															mode="single"
															selected={field.value}
															onSelect={field.onChange}
															autoFocus
															locale={
																language?.startsWith('es') ? esRdp : undefined
															}
														/>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>

									<span className="text-muted-foreground">
										{t('entities.schedule.slot_form.from').toLowerCase()}
									</span>

									{/* Start Time */}
									<FormField
										control={form.control as any}
										name="startTime"
										render={({ field }) => (
											<FormItem>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													value={field.value}
												>
													<FormControl>
														<SelectTrigger className="h-auto px-2 py-1 w-auto border-0 bg-muted/50">
															<SelectValue placeholder="Start">
																{field.value &&
																	DateTime.fromFormat(
																		field.value,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
															</SelectValue>
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														<ScrollArea className="h-60">
															{generateTimeOptions().map((time) => (
																<SelectItem key={time} value={time}>
																	{DateTime.fromFormat(
																		time,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
																</SelectItem>
															))}
														</ScrollArea>
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>

									<span className="text-muted-foreground">
										{t('entities.schedule.slot_form.until').toLowerCase()}
									</span>

									{/* End Time */}
									<FormField
										control={form.control as any}
										name="endTime"
										render={({ field }) => (
											<FormItem>
												<Select
													onValueChange={field.onChange}
													defaultValue={field.value}
													value={field.value}
												>
													<FormControl>
														<SelectTrigger className="h-auto px-2 py-1 w-auto border-0 bg-muted/50">
															<SelectValue placeholder="End">
																{field.value &&
																	DateTime.fromFormat(
																		field.value,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
															</SelectValue>
														</SelectTrigger>
													</FormControl>
													<SelectContent>
														<ScrollArea className="h-60">
															{generateTimeOptions().map((time) => (
																<SelectItem key={time} value={time}>
																	{DateTime.fromFormat(
																		time,
																		'HH:mm'
																	).toLocaleString(DateTime.TIME_SIMPLE)}
																</SelectItem>
															))}
														</ScrollArea>
													</SelectContent>
												</Select>
												<FormMessage />
											</FormItem>
										)}
									/>
								</div>

								{/* Second row: Checkbox and Repeat button */}
								<div className="flex items-center flex-row justify-between">
									{/* Repeat Button */}
									<FormField
										control={form.control as any}
										name="repeatDays"
										render={({ field }) => (
											<FormItem>
												<RepeatButton
													value={field.value}
													onChange={field.onChange}
												/>
												<FormMessage />
											</FormItem>
										)}
									/>

									{/* Custom End Date Checkbox - only shown when repeat is activated */}
									{form.watch('repeatDays').length > 0 && (
										<FormField
											control={form.control as any}
											name="customEndDate"
											render={({ field }) => (
												<FormItem className="flex flex-row items-center space-x-2 space-y-0">
													<FormControl>
														<Checkbox
															checked={field.value}
															onCheckedChange={field.onChange}
														/>
													</FormControl>
													<FormLabel className="text-sm font-normal cursor-pointer">
														{t(
															'entities.schedule.slot_form.choose_custom_end_date'
														)}
													</FormLabel>
												</FormItem>
											)}
										/>
									)}
								</div>

								{/* End Date Picker (shown when custom end date is checked) */}
								{form.watch('customEndDate') && (
									<FormField
										control={form.control as any}
										name="endDate"
										render={({ field }) => (
											<FormItem className="flex flex-col">
												<Popover>
													<PopoverTrigger asChild>
														<FormControl>
															<Button
																variant="outline"
																className="w-full justify-start text-left font-normal h-9 text-sm"
															>
																<CalendarIcon className="mr-2 h-4 w-4" />
																{field.value ? (
																	format(field.value, 'EEEE, d MMM', {
																		locale: language?.startsWith('es')
																			? esDateFns
																			: undefined,
																	})
																) : (
																	<span>
																		{t(
																			'entities.schedule.slot_form.pick_end_date'
																		)}
																	</span>
																)}
															</Button>
														</FormControl>
													</PopoverTrigger>
													<PopoverContent className="w-auto p-0" align="start">
														<Calendar
															mode="single"
															selected={field.value}
															onSelect={field.onChange}
															autoFocus
															locale={
																language?.startsWith('es') ? esRdp : undefined
															}
														/>
													</PopoverContent>
												</Popover>
												<FormMessage />
											</FormItem>
										)}
									/>
								)}
							</div>

							<Separator />

							{/* Playlist Search and Selection */}
							<div className="px-6 py-4">
								<FormField
									control={form.control as any}
									name="playlists"
									render={({ field }) => (
										<FormItem>
											<PlaylistSelector
												value={field.value}
												onChange={field.onChange}
												onPercentagesChange={(percentages) =>
													form.setValue('playlistPercentages', percentages)
												}
												onPlaylistsChange={(playlists) => {
													const newMap: Record<number, Playlist> = {}
													playlists.forEach((p) => {
														newMap[p.id] = p
													})
													setSelectedPlaylistsMap(newMap)
												}}
												playlists={allPlaylists}
												isLoading={isLoading || isFetching}
												hasMorePages={hasMorePages}
												onLoadMore={loadMore}
												dialogOpen={eventEditOpen}
												searchQuery={searchQuery}
												onSearchQueryChange={setSearchQuery}
												customPlaylistIds={customPlaylistIds}
											/>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							{/* Playback Percentage (only shown when 2 playlists selected) */}
							{form.watch('playlists').length === 2 && (
								<div className="px-6 pb-3">
									<div className="space-y-2">
										<div className="flex items-center gap-2">
											<h4 className="text-sm font-medium">
												{t('entities.schedule.slot_form.playback_percentage')}
											</h4>
											<Info className="h-3 w-3 text-muted-foreground" />
										</div>
										<div className="flex gap-2">
											{form.watch('playlists').map((playlistId) => {
												const playlist = allPlaylists.find(
													(p) => p.id === playlistId
												)
												if (!playlist) return null

												const currentPercentages =
													form.watch('playlistPercentages') || {}
												const percentage = currentPercentages[playlistId] || 50

												return (
													<div
														key={playlistId}
														className="flex-1 flex items-center gap-2 px-2 py-1.5 border rounded-lg bg-muted/50"
													>
														<span
															className="text-xs font-medium flex-1 truncate max-w-[100px]"
															title={playlist.title}
														>
															{playlist.title}
														</span>
														{/**
										 			     *
														 * <button
															type="button"
															onClick={() => {
																const newPercentages = { ...currentPercentages }
																const otherPlaylistId = form
																	.watch('playlists')
																	.find((id) => id !== playlistId)
																const newValue = Math.max(0, percentage - 10)
																newPercentages[playlistId] = newValue
																if (otherPlaylistId) {
																	newPercentages[otherPlaylistId] =
																		100 - newValue
																}
																form.setValue(
																	'playlistPercentages',
																	newPercentages
																)
															}}
															disabled={true}
															className="h-5 w-5 rounded-full border flex items-center justify-center hover:bg-background transition-colors flex-shrink-0 cursor-not-allowed"
														>
															<Minus className="h-3 w-3" />
														</button>
														*/}
														<span className="text-xs font-semibold w-10 text-center flex-shrink-0">
															{percentage}%
														</span>
														{/**
														 * <button
															type="button"
															onClick={() => {
																const newPercentages = { ...currentPercentages }
																const otherPlaylistId = form
																	.watch('playlists')
																	.find((id) => id !== playlistId)
																const newValue = Math.min(100, percentage + 10)
																newPercentages[playlistId] = newValue
																if (otherPlaylistId) {
																	newPercentages[otherPlaylistId] =
																		100 - newValue
																}
																form.setValue(
																	'playlistPercentages',
																	newPercentages
																)
															}}
															disabled={true}
															className="h-5 w-5 rounded-full border flex items-center justify-center hover:bg-background transition-colors flex-shrink-0 cursor-not-allowed"
														>
															<Plus className="h-3 w-3" />
														</button>
														 */}
													</div>
												)
											})}
										</div>
									</div>
								</div>
							)}

							{/* Playlist Volume (always visible) */}
							<div className="px-6 pb-4" style={{ display: 'none' }}>
								{form.watch('playlists').length === 2 && (
									<Separator className="mb-3" />
								)}
								<FormField
									control={form.control as any}
									name="volume"
									render={({ field }) => (
										<FormItem className="space-y-2">
											<div className="flex items-center gap-2">
												<FormLabel className="text-sm font-medium">
													{t('entities.schedule.slot_form.playlist_volume')}
												</FormLabel>
												<Info className="h-3 w-3 text-muted-foreground" />
											</div>
											<div className="flex items-center justify-between gap-3">
												<Volume2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
												<FormControl>
													<Slider
														value={[field.value]}
														onValueChange={(value) => field.onChange(value[0])}
														max={100}
														min={0}
														step={1}
														className="flex-1"
													/>
												</FormControl>
												<span className="text-sm font-semibold w-8 text-right flex-shrink-0">
													{field.value}
												</span>
											</div>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<Separator />

							{/* Footer Buttons */}
							<DialogFooter className="px-6 py-4 flex justify-between">
								<Button
									type="button"
									variant="destructive"
									onClick={handleDeleteClick}
									className="mr-auto h-8 rounded-full px-6"
								>
									<Trash2 className="h-4 w-4 mr-2" />
									{t('entities.schedule.slot_form.delete_slot')}
								</Button>
								<div className="flex gap-2">
									<Button
										type="button"
										variant="outline"
										onClick={() => handleEditCancellation()}
										className="h-8 rounded-full px-6"
									>
										{t('actions.back')}
									</Button>
									<Button type="submit" className="h-8 rounded-full px-6">
										{t('actions.continue')}
									</Button>
								</div>
							</DialogFooter>
						</form>
					</Form>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{t('entities.schedule.delete_dialog.title')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{t('entities.schedule.delete_dialog.description')}
						</AlertDialogDescription>
					</AlertDialogHeader>

					<div className="py-4 space-y-3">
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('this')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'this' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'this' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.this_event')}
							</span>
						</div>
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('following')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'following' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'following' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.this_and_following')}
							</span>
						</div>
						<div
							className="flex items-center space-x-3 cursor-pointer"
							onClick={() => setDeleteOption('all')}
						>
							<div
								className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${deleteOption === 'all' ? 'border-primary' : 'border-muted-foreground'}`}
							>
								{deleteOption === 'all' && (
									<div className="w-2.5 h-2.5 rounded-full bg-primary" />
								)}
							</div>
							<span className="font-normal">
								{t('entities.schedule.delete_dialog.all_events')}
							</span>
						</div>
					</div>

					<AlertDialogFooter>
						<AlertDialogCancel className="h-8 rounded-full px-6">
							{t('actions.back')}
						</AlertDialogCancel>
						<AlertDialogAction
							onClick={handleDelete}
							className="bg-destructive text-destructive-foreground hover:bg-destructive/90 h-8 rounded-full px-6"
						>
							{t('actions.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			{/* Multiple Overlaps Warning Modal */}
			<MultiOverlapWarningModal
				open={multiOverlapWarningOpen}
				onOpenChange={(open) => {
					setMultiOverlapWarningOpen(open)
					if (!open) handleBackMultiOverlap()
				}}
				overlapCount={multiOverlapDetails.length}
				onBack={handleBackMultiOverlap}
				onContinue={handleContinueMultiOverlap}
			/>

			{/* Edit Overlap Confirmation Modal */}
			<EditOverlapConfirmationModal
				open={editOverlapModalOpen}
				onOpenChange={setEditOverlapModalOpen}
				overlapDetails={editOverlapDetails}
				editingEventTitle={
					event?.title || t('entities.schedule.slot_form.regular_slot')
				}
				onConfirmUpdate={handleOverlapConfirmUpdate}
				onConfirmDelete={handleOverlapConfirmDelete}
				onCancel={handleOverlapCancel}
			/>
		</>
	)
}
